import {
  getActiveConversation,
  getConversationPhone,
  hasCanonicalCaughtUp,
  matchesSelectedConversation,
  mergeConversationsPreservingOptimisticUnread,
  mergePlaceholderConversations,
  sortConversationsChronologically,
  sortMessagesChronologically,
} from 'app/lib/inboxConversations'
import type { ConversationType, InboxAction, InboxState } from 'app/lib/inboxTypes'

export function inboxReducer(state: InboxState, action: InboxAction): InboxState {
  switch (action.type) {
    case `SET_LOADING`:
      return { ...state, isLoading: action.isLoading }
    case `UPDATE_OPTIMISTIC_MESSAGES`:
      return { ...state, optimisticMessages: action.updater(state.optimisticMessages) }
    case `UPDATE_UNREAD_LOCATIONS`:
      return { ...state, unreadLocations: action.updater(state.unreadLocations) }
    case `UPDATE_CONVERSATIONS`:
      return { ...state, conversations: sortConversationsChronologically(action.updater(state.conversations)) }
    case `SELECT_CONVERSATION`:
      return {
        ...state,
        isLoading: true,
        selectedThreadId: action.threadId,
        selectedContactId: action.contactId,
        selectedContact: action.contact,
        messages: undefined,
        optimisticMessages: undefined,
      }
    case `ADD_CONVERSATION`: {
      const hasExistingConversation = state.conversations.some(
        (conversation) =>
          (!!action.threadId && conversation.threadId === action.threadId) ||
          (!!action.contact.id && conversation.contactId === action.contact.id) ||
          // A typed-in number has no id; the phone is its only distinguishing handle.
          (!!action.contact.cellPhone && getConversationPhone(conversation) === action.contact.cellPhone),
      )
      const placeholderConversation: ConversationType = {
        contactId: action.contact.id,
        from: action.contact.cellPhone,
        locationId: action.contact.locationId,
        total: 0,
        threadId: action.threadId,
        unread: 0,
        archived: false,
      }
      // With no thread or contact id there is nothing to fetch, so start `messages` empty, not undefined.
      const hasFetchableThread = Boolean(action.threadId || action.contact.id)

      return {
        ...state,
        isLoading: true,
        selectedThreadId: action.threadId ?? ``,
        selectedContactId: action.contact.id,
        selectedContact: action.contact,
        messages: hasFetchableThread ? undefined : [],
        optimisticMessages: undefined,
        conversations: hasExistingConversation
          ? state.conversations
          : [placeholderConversation].concat(state.conversations),
      }
    }
    case `RESET_INBOX`:
      return {
        ...state,
        selectedThreadId: ``,
        selectedContactId: undefined,
        selectedContact: undefined,
        optimisticMessages: undefined,
        optimisticUnreadThreads: {},
        conversations: [],
      }
    case `BEGIN_MUTATION`:
      return { ...state, pendingMutationCount: state.pendingMutationCount + 1 }
    case `END_MUTATION`:
      return { ...state, pendingMutationCount: Math.max(0, state.pendingMutationCount - 1) }
    case `SYNC_APPLIED`: {
      const canonicalCaughtUp = hasCanonicalCaughtUp(action.messages, state.optimisticMessages)
      const mergedConversations = canonicalCaughtUp
        ? action.conversations
        : mergeConversationsPreservingOptimisticUnread(
            state.conversations,
            action.conversations,
            state.selectedThreadId,
            state.selectedContactId,
          )
      // Holds outlive this sync until the index catches up; a thread that left the list drops its hold.
      const canonicalUnreadByThread = new Map(action.conversations.map((c) => [c.threadId, c.unread]))
      const heldUnreadThreads = Object.fromEntries(
        Object.entries(state.optimisticUnreadThreads).filter(
          ([heldThreadId]) => canonicalUnreadByThread.get(heldThreadId) === 0,
        ),
      )
      const heldConversations = Object.keys(heldUnreadThreads).length
        ? mergedConversations.map((conversation) =>
            conversation.threadId && heldUnreadThreads[conversation.threadId] != null
              ? { ...conversation, read: false, unread: heldUnreadThreads[conversation.threadId] }
              : conversation,
          )
        : mergedConversations
      const countsCaughtUp = canonicalCaughtUp && Object.keys(heldUnreadThreads).length === 0

      return {
        ...state,
        isLoading: false,
        hasSynced: true,
        conversations: mergePlaceholderConversations(state.conversations, heldConversations),
        unreadLocations: countsCaughtUp ? action.unreadLocations : (state.unreadLocations ?? action.unreadLocations),
        messages: action.messages,
        optimisticMessages: canonicalCaughtUp ? undefined : state.optimisticMessages,
        optimisticUnreadThreads: heldUnreadThreads,
      }
    }
    case `SYNC_FAILED`:
      return { ...state, isLoading: false, hasSynced: true }
    case `SYNC_CLEARED`:
      return {
        ...state,
        isLoading: false,
        conversations: [],
        unreadLocations: {},
        messages: [],
        optimisticMessages: undefined,
        optimisticUnreadThreads: {},
      }
    case `LOCATION_CHANGED`:
      // Reset to pre-sync state so the skeleton shows again for the new location's first sync.
      return {
        ...state,
        hasSynced: false,
        isLoading: true,
        selectedThreadId: ``,
        selectedContactId: undefined,
        selectedContact: undefined,
        conversations: [],
        messages: undefined,
        unreadLocations: undefined,
        optimisticMessages: undefined,
        optimisticUnreadThreads: {},
      }
    case `MARK_READ_OPTIMISTIC`: {
      const activeConversation = getActiveConversation(state)
      const previousUnread = activeConversation?.unread ?? 0
      const updatedIds = new Set(action.updatedIds)
      const nextMessages = sortMessagesChronologically(
        [...(state.optimisticMessages ?? state.messages ?? [])].map((message) =>
          updatedIds.has(message.id!) ? { ...message, read: action.read } : message,
        ),
      )

      // The hold keeps the badge steady after the user leaves the thread and optimisticMessages drops.
      const nextHolds = { ...state.optimisticUnreadThreads }
      if (activeConversation?.threadId) {
        if (action.read) delete nextHolds[activeConversation.threadId]
        else nextHolds[activeConversation.threadId] = action.unread
      }

      return {
        ...state,
        optimisticUnreadThreads: nextHolds,
        optimisticMessages: nextMessages,
        conversations: sortConversationsChronologically(
          state.conversations.map((conversation) => {
            if (!matchesSelectedConversation(conversation, state.selectedThreadId, state.selectedContactId)) {
              return conversation
            }
            return { ...conversation, read: action.unread === 0, unread: action.unread }
          }),
        ),
        unreadLocations: action.locationId
          ? {
              ...(state.unreadLocations ?? {}),
              [action.locationId]: Math.max(
                0,
                ((state.unreadLocations ?? {})[action.locationId] ?? 0) +
                  (Number(action.unread > 0) - Number(previousUnread > 0)),
              ),
            }
          : state.unreadLocations,
      }
    }
    case `SEND_MESSAGE_OPTIMISTIC`: {
      const currentMessages = [...(state.optimisticMessages ?? state.messages ?? [])]
      const nextMessages = sortMessagesChronologically(
        currentMessages
          .map((message) =>
            message.createdDate! <= action.message.createdDate! && message.sentByUserId == null && !message.isAgent
              ? { ...message, read: true }
              : message,
          )
          .concat(
            currentMessages.some((message) => message.id === action.message.id)
              ? []
              : [{ ...action.message, read: true }],
          ),
      )

      // Replying is an explicit "I'm done here", so it retires the hold.
      const sentThreadId = action.currentThreadId ?? getActiveConversation(state)?.threadId
      const holdsAfterSend = { ...state.optimisticUnreadThreads }
      if (sentThreadId) delete holdsAfterSend[sentThreadId]

      return {
        ...state,
        optimisticUnreadThreads: holdsAfterSend,
        optimisticMessages: nextMessages,
        conversations: sortConversationsChronologically(
          state.conversations.map((conversation) => {
            if (
              !matchesSelectedConversation(
                conversation,
                state.selectedThreadId,
                state.selectedContactId,
                action.currentThreadId,
              )
            ) {
              return conversation
            }

            return {
              ...conversation,
              text: action.message.text,
              createdDate: action.message.createdDate,
              read: true,
              unread: Math.max(0, (conversation.unread || 0) - action.newlyReadCount),
            }
          }),
        ),
        unreadLocations: action.locationId
          ? {
              ...(state.unreadLocations ?? {}),
              [action.locationId]: Math.max(
                0,
                ((state.unreadLocations ?? {})[action.locationId] ?? 0) - Number(action.newlyReadCount > 0),
              ),
            }
          : state.unreadLocations,
      }
    }
    case `SET_THREAD_UNREAD_OPTIMISTIC`: {
      const previousUnread = state.conversations.find((c) => c.threadId === action.threadId)?.unread ?? 0
      const nextHolds = { ...state.optimisticUnreadThreads }
      if (action.unread > 0) nextHolds[action.threadId] = action.unread
      else delete nextHolds[action.threadId]

      return {
        ...state,
        optimisticUnreadThreads: nextHolds,
        conversations: state.conversations.map((conversation) =>
          conversation.threadId === action.threadId
            ? { ...conversation, read: action.unread === 0, unread: action.unread }
            : conversation,
        ),
        unreadLocations: action.locationId
          ? {
              ...(state.unreadLocations ?? {}),
              [action.locationId]: Math.max(
                0,
                ((state.unreadLocations ?? {})[action.locationId] ?? 0) +
                  (Number(action.unread > 0) - Number(previousUnread > 0)),
              ),
            }
          : state.unreadLocations,
      }
    }
    case `ARCHIVE_LOCAL_REMOVE`: {
      const holdsAfterArchive = { ...state.optimisticUnreadThreads }
      delete holdsAfterArchive[action.threadId]
      return {
        ...state,
        optimisticUnreadThreads: holdsAfterArchive,
        conversations: sortConversationsChronologically(
          state.conversations.filter((conversation) => conversation.threadId !== action.threadId),
        ),
        ...(state.selectedThreadId === action.threadId
          ? {
              selectedThreadId: ``,
              selectedContactId: undefined,
              selectedContact: undefined,
              optimisticMessages: undefined,
            }
          : {}),
      }
    }
    case `SET_RESOLVED_SELECTION`: {
      const threadChanged = state.selectedThreadId !== action.threadId
      return {
        ...state,
        selectedThreadId: action.threadId,
        selectedContactId: action.contactId,
        selectedContact: action.selectedContact,
        ...(threadChanged
          ? {
              messages: undefined,
              isLoading: true,
              optimisticMessages: undefined,
            }
          : {}),
      }
    }
    case `CLEAR_RESOLVED_SELECTION`:
      return {
        ...state,
        selectedThreadId: ``,
        selectedContactId: undefined,
        selectedContact: undefined,
        optimisticMessages: undefined,
      }
    default:
      return state
  }
}
